import { useState, useRef, useCallback, useEffect } from "react";
import { Paperclip, Tag, Type, MoreHorizontal, ArrowUp } from "lucide-react";
import { useBrief } from "@gbougakov/brief-ts/react";

export function NoteInput() {
  const client = useBrief();
  const [content, setContent] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // Max ~6 lines (24px line-height * 6 = 144px)
    el.style.height = `${Math.min(el.scrollHeight, 144)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [content, adjustHeight]);

  const send = useCallback(async () => {
    const text = content.trim();
    if (!text) return;

    setContent("");
    try {
      await client.createNote({ content: text, isPlainText: true });
    } catch (e) {
      console.error("Failed to create note:", e);
      setContent(text);
    }
  }, [content, client]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        send();
      }
    },
    [send],
  );

  const hasContent = content.trim().length > 0;

  return (
    <div
      className="shrink-0"
      /*style={{ paddingBottom: "min(1rem, env(safe-area-inset-bottom))" }}*/
    >
      <div className="bg-surface-container-highest rounded-3xl rounded-b-none overflow-hidden p-4 flex flex-col gap-3">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Send yourself something..."
          rows={1}
          className="w-full bg-transparent text-on-surface placeholder:text-on-surface-variant text-base leading-6 tracking-wide resize-none outline-none"
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <button className="text-on-surface-variant hover:text-on-surface transition-colors">
              <Paperclip className="w-6 h-6" />
            </button>
            <button className="text-on-surface-variant hover:text-on-surface transition-colors">
              <Tag className="w-6 h-6" />
            </button>
            <button className="text-on-surface-variant hover:text-on-surface transition-colors">
              <Type className="w-6 h-6" />
            </button>
            <button className="text-on-surface-variant hover:text-on-surface transition-colors">
              <MoreHorizontal className="w-6 h-6" />
            </button>
          </div>

          <button
            onClick={send}
            disabled={!hasContent}
            className="bg-primary rounded-lg p-1 disabled:opacity-30 transition-opacity"
          >
            <ArrowUp className="w-5 h-5 text-primary-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
}
